import type { CredentialResponse } from "@react-oauth/google";
import type { QueryClient } from "@tanstack/react-query";
import { Cookies } from "react-cookie";
import { decodeToken } from "react-jwt";

import { StaticValue } from "../../seed/constants";
import { applyAuthorizationHeader } from "../apiService";
import { setAuthorizationToken } from "./authToken";

const staticValues = new StaticValue().getDefaultValues();

export interface GoogleProfile {
	email: string;
	firstName: string;
	lastName: string;
	picture: string;
}

type GoogleIdTokenPayload = Omit<GoogleProfile, "firstName" | "lastName"> & {
	given_name: string;
	family_name: string;
};

export interface GoogleSession {
	credential: string;
	profile: GoogleProfile;
	publish: () => void;
}

const CREDENTIAL_LIFETIME_DAYS = 30;

export function establishGoogleSession(googleUser: CredentialResponse, queryClient: QueryClient): GoogleSession | null {
	if (!googleUser.credential) return null;

	const credential = `GO::${googleUser.credential}`;

	queryClient.clear();
	new Cookies().remove(staticValues.localStorageResumeId, { path: "/" });
	localStorage.removeItem("resumeList");

	applyAuthorizationHeader(credential);

	const payload = decodeToken<GoogleIdTokenPayload>(googleUser.credential);

	return {
		credential,
		publish: () => {
			const expireDate = new Date();
			expireDate.setDate(expireDate.getDate() + CREDENTIAL_LIFETIME_DAYS);
			setAuthorizationToken(credential, expireDate);
		},
		profile: {
			email: payload.email,
			firstName: payload.given_name,
			lastName: payload.family_name,
			picture: payload.picture,
		},
	};
}
